import { useCallback } from 'react'
import { GridApi, RowGroupOpenedEvent } from 'ag-grid-enterprise'
import {
  EnhancedStatementRow,
  FinancialRowType,
  transformDimensionResponseToEnhancedRows
} from '../utils/dataEnhancement'
import { fetchDimensionData } from '../queries/useDimensionDataQuery'
import { Dimension } from '../types'
import { promiseAllWithLimit } from '../utils/promiseUtils'
import { chunkArray } from '../../../utils/helpers'

// Maximum number of concurrent dimension data requests
const MAX_CONCURRENT_DIMENSION_REQUESTS = 5
// Maximum number of dimension IDs per API request
const DIMENSIONS_PER_BATCH = 10

/**
 * Fetch dimension data for all expanded dimension groups/dimensions
 * Returns array of dimension rows without applying to grid
 *
 * @param gridApi - AG Grid API instance
 * @param accountCode - Optional: if provided, only fetch dimensions for this account code
 * @param getDimensionParams - Function to build dimension query params from row data
 * @param dimensionMap - Map of dimension hierarchies
 * @param dimensionList - List of all dimensions
 * @returns Array of dimension rows to update
 */
export const fetchExpandedDimensionRows = async (
  gridApi: GridApi<EnhancedStatementRow> | undefined,
  accountCode: number | undefined,
  getDimensionParams: (row: EnhancedStatementRow) => any,
  dimensionMap: Map<string | null, Dimension[]> | undefined,
  dimensionList: Dimension[]
): Promise<EnhancedStatementRow[]> => {
  if (!gridApi) return []

  const expandedNodes: any[] = []

  // Find all expanded dimension group/dimension nodes
  gridApi.forEachNode(node => {
    if (!node.expanded || !node.data) return

    const isTargetType =
      node.data.rowType === FinancialRowType.DIMENSION_GROUP || node.data.rowType === FinancialRowType.DIMENSION

    if (!isTargetType) return

    // Filter by accountCode if provided
    if (accountCode !== undefined && node.data._sourceAccountCode !== accountCode) return

    expandedNodes.push(node)
  })

  // Create fetch tasks (functions that return promises)
  // Each node may have multiple dimensionIds - we batch them into groups
  const fetchTasks = expandedNodes.flatMap(node => {
    try {
      // Get dimension params
      const dimensionParams = getDimensionParams(node.data)

      // If no dimensionIds, return empty result
      if (!dimensionParams.dimensionIds || dimensionParams.dimensionIds.length === 0) {
        return [async () => []]
      }

      // Split dimensionIds into batches (e.g., if 20 dimensions → [8, 8, 4])
      const dimensionBatches = chunkArray(dimensionParams.dimensionIds, DIMENSIONS_PER_BATCH)

      // Create one fetch task per batch
      return dimensionBatches.map(batchDimensionIds => async () => {
        try {
          // Fetch with batched dimensionIds
          const batchParams = {
            ...dimensionParams,
            dimensionIds: batchDimensionIds
          }

          const dimensionResponse = await fetchDimensionData(batchParams)

          // Transform to enhanced rows
          const enhancedRows = transformDimensionResponseToEnhancedRows(
            dimensionResponse,
            batchParams,
            dimensionMap,
            dimensionList
          )

          return enhancedRows
        } catch (error) {
          console.error('❌ Error fetching dimension batch for node:', node.data.key, error)
          return []
        }
      })
    } catch (error) {
      console.error('❌ Error preparing dimension params for node:', node.data.key, error)
      return [async () => []]
    }
  })

  // Execute with concurrency limit to avoid overloading browser/backend
  const allNewRows = await promiseAllWithLimit(fetchTasks, MAX_CONCURRENT_DIMENSION_REQUESTS)

  // Flatten all rows
  return allNewRows.flat()
}

export const useDimensionLoading = (
  gridApi: GridApi<EnhancedStatementRow> | undefined,
  getDimensionParams: (row: EnhancedStatementRow) => any,
  dimensionMap: Map<string | null, Dimension[]> | undefined,
  dimensionList: Dimension[]
) => {
  const onRowGroupOpened = useCallback(
    async (event: RowGroupOpenedEvent<EnhancedStatementRow>) => {
      const rowNode = event.node
      const rowNodeData = rowNode.data

      if (!rowNodeData) return

      // Skip if children are already loaded
      if ((rowNode.allChildrenCount ?? 0) > 0) {
        return
      }

      // Only load for dimension groups and dimensions
      const shouldLoadChildren =
        rowNodeData.rowType === FinancialRowType.DIMENSION_GROUP || rowNodeData.rowType === FinancialRowType.DIMENSION

      if (!shouldLoadChildren) return

      // Set loading state
      rowNode.setData({ ...rowNodeData, isLoading: true })

      let shouldExpand = true

      try {
        const dimensionParams = getDimensionParams(rowNodeData)

        // If no dimensionIds, skip
        if (!dimensionParams.dimensionIds || dimensionParams.dimensionIds.length === 0) {
          shouldExpand = false
          return
        }

        // Split dimensionIds into batches to avoid overloading backend
        const dimensionBatches = chunkArray(dimensionParams.dimensionIds, DIMENSIONS_PER_BATCH)

        // Fetch all batches with concurrency limit
        const fetchTasks = dimensionBatches.map(batchDimensionIds => async () => {
          try {
            const batchParams = {
              ...dimensionParams,
              dimensionIds: batchDimensionIds
            }

            const dimensionResponse = await fetchDimensionData(batchParams)
            const enhancedRows = transformDimensionResponseToEnhancedRows(
              dimensionResponse,
              batchParams,
              dimensionMap,
              dimensionList
            )

            return enhancedRows
          } catch (error) {
            console.error('❌ Error fetching dimension batch:', {
              batchDimensionIds,
              accountCode: dimensionParams.accountCode,
              error
            })
            return []
          }
        })

        // Execute batches with concurrency limit
        const allBatchResults = await promiseAllWithLimit(fetchTasks, MAX_CONCURRENT_DIMENSION_REQUESTS)
        const enhancedRows = allBatchResults.flat()

        // Add dimension rows to grid
        gridApi?.applyTransaction({ add: enhancedRows })
      } catch (error) {
        console.error('Error loading dimension children:', error)
        shouldExpand = false
      } finally {
        // Clear loading state and expand only if we have data
        rowNode.setData({ ...rowNodeData, isLoading: false })
        gridApi?.setRowNodeExpanded(rowNode, shouldExpand)
        gridApi?.onFilterChanged()
      }
    },
    [gridApi, getDimensionParams, dimensionMap, dimensionList]
  )

  return onRowGroupOpened
}
